import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import StatusDialog from '../components/StatusDialog';

const StatusContext = createContext();

export const useStatus = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
};

export const StatusProvider = ({ children }) => {
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    status: 'info',
    details: null,
    actions: null
  });
  
  const timeoutRef = useRef(null);

  const showStatus = (title, message, status = 'info', details = null, actions = null, autoClose = true) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setDialog({
      isOpen: true,
      title,
      message,
      status,
      details,
      actions
    });
    
    // Auto-close after 10 seconds for success messages, 15 seconds for others
    if (autoClose) {
      const timeout = status === 'success' ? 10000 : 15000;
      timeoutRef.current = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('StatusContext: Auto-closing dialog after timeout');
        }
        hideStatus();
      }, timeout);
    }
  };

  const showSuccess = (title, message, details = null, actions = null) => {
    showStatus(title, message, 'success', details, actions);
  };

  const showError = (title, message, details = null, actions = null) => {
    showStatus(title, message, 'error', details, actions);
  };

  const showWarning = (title, message, details = null, actions = null) => {
    showStatus(title, message, 'warning', details, actions);
  };

  const showLoading = (title, message, details = null) => {
    showStatus(title, message, 'loading', details);
  };

  const hideStatus = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('StatusContext: hideStatus called');
    }
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setDialog(prev => {
      if (process.env.NODE_ENV === 'development') {
        console.log('StatusContext: Previous dialog state:', prev);
      }
      const newState = { ...prev, isOpen: false };
      if (process.env.NODE_ENV === 'development') {
        console.log('StatusContext: New dialog state:', newState);
      }
      return newState;
    });
  };

  const forceClose = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('StatusContext: forceClose called');
    }
    hideStatus();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = {
    showStatus,
    showSuccess,
    showError,
    showWarning,
    showLoading,
    hideStatus,
    forceClose,
    dialog
  };

  return (
    <StatusContext.Provider value={value}>
      {children}
      <StatusDialog
        isOpen={dialog.isOpen}
        onClose={hideStatus}
        title={dialog.title}
        message={dialog.message}
        status={dialog.status}
        details={dialog.details}
        actions={dialog.actions}
      />
    </StatusContext.Provider>
  );
};
